// The inventory (decision 0053: "a list in tabs (weapons, materials, consumables, key items), stacks with caps, no grid
// and no weight"). Stacks with caps from the item table; a limit on the number of stacks, either one for the whole
// inventory (phase C's bag) or one per tab (`tabs`: each item sorts into its row's tab). Add, count, take. Its state is
// plain data the game keeps (a kernel component), so saves and hashes cover it, and an add never loses an item: what
// doesn't fit comes back to the caller (a pickup stays on the ground).
import type { ItemTable } from "./items";

/** One stack. */
export interface ItemStack {
  item: string;
  count: number;
}

/** The inventory's state: stacks in the order they were made, and how many stacks it holds at most: in all
 *  (`maxStacks`), or per tab when `tabs` is given (tab id → its most stacks; a tab not named holds none). */
export interface InventoryState {
  stacks: ItemStack[];
  maxStacks: number;
  tabs?: Record<string, number>;
}

/** A new, empty inventory state; with `tabs`, each tab holds its own number of stacks (`maxStacks` is their sum). */
export const emptyInventory = (maxStacks: number, tabs?: Readonly<Record<string, number>>): InventoryState =>
  tabs ? { stacks: [], maxStacks: Object.values(tabs).reduce((a, b) => a + b, 0), tabs: { ...tabs } } : { stacks: [], maxStacks };

/** The inventory's operations over a state, with the item table's stack caps and tabs. */
export interface Inventory {
  /** Add `count` of an item: existing stacks fill to their cap first, then new stacks while its tab (or the inventory)
   *  has room. Returns how many did NOT fit (0 when all went in). Throws on an unknown item or a count that isn't a
   *  whole number ≥ 0. */
  add(s: InventoryState, item: string, count: number): number;
  /** How many of an item it holds. */
  count(s: InventoryState, item: string): number;
  /** Take `count` of an item (from the last stacks first); all or nothing: false, and nothing taken, when it holds
   *  fewer. */
  take(s: InventoryState, item: string, count: number): boolean;
  /** How many more of an item would fit. */
  room(s: InventoryState, item: string): number;
  /** Whether every one of these would fit together (a craft's or a purchase's outputs), after `taking` is taken. */
  fits(s: InventoryState, adds: readonly ItemStack[], taking?: readonly ItemStack[]): boolean;
  /** The stacks in a tab, in order (every stack when the state has no tabs and `tab` is null). */
  tab(s: InventoryState, tab: string | null): ItemStack[];
  /** How many stacks a tab holds, and its limit. */
  tabUse(s: InventoryState, tab: string): { used: number; max: number };
  /** Problems with a state (counts over a cap, empty stacks, unknown items, too many stacks in all or in a tab);
   *  empty when sound. */
  check(s: InventoryState): string[];
}

/** The inventory over an item table. */
export function createInventory(items: ItemTable): Inventory {
  const cap = (item: string): number => items.get(item).stack;
  const whole = (n: number): void => {
    if (!Number.isInteger(n) || n < 0) throw new Error(`inventory: count ${n} is not a whole number ≥ 0`);
  };
  const tabOf = (item: string): string => items.tabOf(item);
  /** Stacks it may still start for this item (its tab's room, or the whole inventory's). */
  const free = (s: InventoryState, item: string): number => {
    if (!s.tabs) return s.maxStacks - s.stacks.length;
    const t = tabOf(item);
    return (s.tabs[t] ?? 0) - s.stacks.filter((st) => tabOf(st.item) === t).length;
  };
  const inv: Inventory = {
    add(s, item, count) {
      whole(count);
      const c = cap(item);
      let left = count;
      for (const st of s.stacks) {
        if (left === 0) break;
        if (st.item !== item || st.count >= c) continue;
        const n = Math.min(c - st.count, left);
        st.count += n;
        left -= n;
      }
      let room = free(s, item);
      while (left > 0 && room > 0) {
        const n = Math.min(c, left);
        s.stacks.push({ item, count: n });
        left -= n;
        room--;
      }
      return left;
    },
    count: (s, item) => s.stacks.reduce((n, st) => (st.item === item ? n + st.count : n), 0),
    take(s, item, count) {
      whole(count);
      if (inv.count(s, item) < count) return false;
      let left = count;
      for (let i = s.stacks.length - 1; i >= 0 && left > 0; i--) {
        const st = s.stacks[i]!;
        if (st.item !== item) continue;
        const n = Math.min(st.count, left);
        st.count -= n;
        left -= n;
      }
      s.stacks = s.stacks.filter((st) => st.count > 0);
      return true;
    },
    room(s, item) {
      const c = cap(item);
      const partial = s.stacks.reduce((n, st) => (st.item === item ? n + (c - st.count) : n), 0);
      return partial + Math.max(0, free(s, item)) * c;
    },
    fits(s, adds, taking = []) {
      const trial: InventoryState = structuredClone(s);
      for (const t of taking) if (!inv.take(trial, t.item, t.count)) return false;
      for (const a of adds) if (inv.add(trial, a.item, a.count) > 0) return false;
      return true;
    },
    tab: (s, tab) => (tab === null ? [...s.stacks] : s.stacks.filter((st) => items.has(st.item) && tabOf(st.item) === tab)),
    tabUse: (s, tab) => ({ used: s.stacks.filter((st) => items.has(st.item) && tabOf(st.item) === tab).length, max: s.tabs ? (s.tabs[tab] ?? 0) : s.maxStacks }),
    check(s) {
      const out: string[] = [];
      if (s.stacks.length > s.maxStacks) out.push(`${s.stacks.length} stacks over the limit of ${s.maxStacks}`);
      if (s.tabs) {
        const used: Record<string, number> = {};
        for (const st of s.stacks) if (items.has(st.item)) used[tabOf(st.item)] = (used[tabOf(st.item)] ?? 0) + 1;
        for (const [t, n] of Object.entries(used)) if (n > (s.tabs[t] ?? 0)) out.push(`tab "${t}" holds ${n} stacks, over its limit of ${s.tabs[t] ?? 0}`);
      }
      for (const st of s.stacks) {
        if (!items.has(st.item)) {
          out.push(`unknown item "${st.item}"`);
          continue;
        }
        if (!Number.isInteger(st.count) || st.count < 1) out.push(`a stack of "${st.item}" holds ${st.count}`);
        if (st.count > cap(st.item)) out.push(`a stack of "${st.item}" holds ${st.count}, over its cap of ${cap(st.item)}`);
      }
      return out;
    },
  };
  return inv;
}
